import { Copy07 } from '@timelyapp/icons/line-icons';
import { Dataflow03 } from '@timelyapp/icons/solid-icons';
import { Button, CustomIcon, Icon, Text } from '@timelyapp/react';
import {
  Bell,
  Clipboard,
  Globe,
  Head,
  Imports,
  Layers,
  Tag,
  Wand,
  Webhooks,
} from 'components/Icons';
import React from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import {
  getHasHourStatesFeature,
  getHasImportManagerFeature,
  getHasProjectTemplatesFeature,
} from 'redux/selectors/accounts';
import {
  getCanManageWorkspaceDefaults,
  getIsUserAdmin,
  getUserPermissions,
} from 'redux/selectors/user';
import { getHasReactSubscriptionPage } from 'utils/bootstrap';
import { canViewTagSettings, canViewWorkspaceSettings } from 'utils/settings';
import RailsLink from './RailsLink';
import styles from './SettingsNav.module.scss';

const SettingsNav: React.FC = () => {
  const permissions = useSelector(getUserPermissions);
  const isAdmin = useSelector(getIsUserAdmin);
  // Owner or admin — who may write AutoSheet workspace defaults.
  const canManageAutoSheetDefaults = useSelector(getCanManageWorkspaceDefaults);
  const hasHourStatesFeature = useSelector(getHasHourStatesFeature);
  const hasImportManagerFeature = useSelector(getHasImportManagerFeature);
  const hasProjectTemplatesFeature = useSelector(getHasProjectTemplatesFeature);
  const hasReactSubscriptionPage = getHasReactSubscriptionPage();
  const isOnSubscriptionPage = !!useMatch('/settings/subscription');
  const navigate = useNavigate();

  const handleBackClick = () => {
    const referrerPath = sessionStorage.getItem('settings_referrer_path') || '/';
    navigate(referrerPath);
  };

  return (
    <nav className={styles.container}>
      <Button
        variant="floating"
        size="small"
        onClick={handleBackClick}
        className={styles.backButton}
      >
        <Icon name="ArrowNarrowLeft" type="line" />
        <Text bold size="small">
          Back to Timely
        </Text>
      </Button>
      <p className={styles.sectionLabel}>Integrations</p>
      <menu>
        <NavLink to="/settings/apps" className={({ isActive }) => (isActive ? styles.active : '')}>
          <CustomIcon.Integration strokeWidth={2} />
          <span>Integrations</span>
        </NavLink>
      </menu>
      <p className={styles.sectionLabel}>Personal</p>
      <menu>
        <NavLink
          to="/settings/identity/edit"
          className={({ isActive }) => (isActive ? styles.active : '')}
        >
          <Head />
          <span>Personal details</span>
        </NavLink>
        <NavLink
          to="/settings/notifications"
          className={({ isActive }) => (isActive ? styles.active : '')}
        >
          <Bell />
          <span>Notifications</span>
        </NavLink>
        <NavLink
          to="/settings/autosheets"
          end
          className={({ isActive }) => (isActive ? styles.active : '')}
        >
          <Wand />
          <span>AutoSheet</span>
        </NavLink>
      </menu>
      <p className={styles.sectionLabel}>Workspace</p>
      <menu>
        {canViewWorkspaceSettings(permissions) && (
          <NavLink
            to="/settings/edit"
            className={({ isActive }) => (isActive ? styles.active : '')}
          >
            <Globe />
            <span>General</span>
          </NavLink>
        )}
        {/* Rails picks ERB or React per account, so without the flag this stays a full page load. */}
        {permissions.canManageSubscriptions &&
          (hasReactSubscriptionPage ? (
            <NavLink
              to="/settings/subscription"
              end
              className={({ isActive }) => (isActive ? styles.active : '')}
            >
              <Clipboard />
              <span>Subscriptions</span>
            </NavLink>
          ) : (
            <RailsLink
              to="/settings/subscription"
              className={isOnSubscriptionPage ? styles.active : undefined}
            >
              <Clipboard />
              <span>Subscriptions</span>
            </RailsLink>
          ))}
        {canViewTagSettings(permissions) && (
          <NavLink
            to="/settings/labels"
            className={({ isActive }) => (isActive ? styles.active : '')}
          >
            <Tag />
            <span data-testid="tags_link">Tags</span>
          </NavLink>
        )}
        {canManageAutoSheetDefaults && (
          <NavLink
            to="/settings/autosheets/workspace"
            className={({ isActive }) => (isActive ? styles.active : '')}
          >
            <Wand />
            <span>AutoSheet</span>
          </NavLink>
        )}
        {hasHourStatesFeature && isAdmin && (
          <NavLink
            to="/settings/workflow"
            className={({ isActive }) => (isActive ? styles.active : '')}
          >
            <Dataflow03 />
            <span>Time entry states</span>
          </NavLink>
        )}
        {hasProjectTemplatesFeature && isAdmin && (
          <NavLink
            to="/settings/project-templates"
            className={({ isActive }) => (isActive ? styles.active : '')}
          >
            <Copy07 />
            <span>Project Templates</span>
          </NavLink>
        )}
      </menu>
      {(permissions.canManageOauthApplications || isAdmin) && (
        <>
          <p className={styles.sectionLabel}>Developer</p>
          <menu>
            {permissions.canManageOauthApplications && (
              <RailsLink to="/oauth_applications">
                <Layers />
                <span>API access</span>
              </RailsLink>
            )}
            {isAdmin && (
              <RailsLink
                to="/settings/webhooks"
                className={({ isActive }) => (isActive ? styles.active : '')}
              >
                <Webhooks />
                <span>Webhooks</span>
              </RailsLink>
            )}
            {isAdmin && hasImportManagerFeature && (
              <RailsLink
                to="/settings/imports"
                className={({ isActive }) => (isActive ? styles.active : '')}
              >
                <Imports />
                <span>Import</span>
              </RailsLink>
            )}
          </menu>
        </>
      )}
    </nav>
  );
};

export default React.memo(SettingsNav);
